import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DebugUiBridge } from '../DebugUiBridge';
import { revealSceneNode } from '../uiActions';

interface WeaponDebugInfo {
  id: string;
  ownerId: string;
  attackDefId: string;
  hitboxEnabled: boolean;
  hasHit: boolean;
  hasDedicatedCollisionMesh: boolean;
  attachedBone?: string;
  weaponMeshName: string;
  collisionMeshName?: string;
  animationGroup: {
    name: string;
    isPlaying: boolean;
    speedRatio: number;
  } | null;
}

interface ProjectileDebugInfo {
  id: string;
  ownerId: string;
  attackDefId: string;
  type: string;
  position: { x: number; y: number; z: number };
  velocity: { x: number; y: number; z: number };
  hasCollided: boolean;
  detonated: boolean;
}

interface CombatDebugState {
  weapons: WeaponDebugInfo[];
  projectiles: ProjectileDebugInfo[];
  recentDamage: Array<{
    attackerId: string;
    targetId: string;
    damage: number;
    timestamp: number;
  }>;
  lastHit?: { nodeId: string; nodeName: string } | null;
}

/**
 * Combat Panel
 * 
 * Shows live combat state:
 * - Active weapons (attacker, hitbox type, animation state)
 * - Active projectiles (type, position, velocity)
 * - Recent damage events
 */
@customElement('combat-panel')
export class CombatPanel extends LitElement {
  static styles = css`
    :host {
      display: block;
      height: 100%;
      color: #eee;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 12px;
    }

    .panel-content {
      display: flex;
      flex-direction: column;
      height: 100%;
      padding: 12px;
      gap: 12px;
      overflow-y: auto;
    }

    .section {
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 4px;
      padding: 8px;
      background: rgba(0, 0, 0, 0.2);
    }

    .section-title {
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      opacity: 0.75;
      margin-bottom: 8px;
      padding-bottom: 6px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .toggle-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      padding: 6px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 4px;
    }

    .toggle-label {
      font-weight: 500;
      flex: 1;
    }

    .toggle-switch {
      position: relative;
      width: 40px;
      height: 20px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 10px;
      cursor: pointer;
      transition: background 0.2s;
    }

    .toggle-switch.on {
      background: rgba(100, 200, 100, 0.5);
    }

    .toggle-switch::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: white;
      top: 2px;
      left: 2px;
      transition: transform 0.2s;
    }

    .toggle-switch.on::after {
      transform: translateX(20px);
    }

    .item {
      padding: 6px 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    .item:last-child {
      border-bottom: none;
    }

    .item-title {
      font-weight: 500;
      color: #9cf;
      margin-bottom: 4px;
    }

    .item-detail {
      font-size: 11px;
      opacity: 0.85;
      margin-left: 8px;
    }

    .badge {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 10px;
      font-weight: 600;
      margin-left: 6px;
    }

    .badge.enabled {
      background: rgba(100, 255, 100, 0.2);
      color: #6f6;
    }

    .badge.disabled {
      background: rgba(150, 150, 150, 0.2);
      color: #aaa;
    }

    .badge.dedicated {
      background: rgba(100, 200, 255, 0.2);
      color: #6cf;
    }

    .badge.fallback {
      background: rgba(255, 200, 100, 0.2);
      color: #fc9;
    }

    .badge.kinematic {
      background: rgba(255, 100, 100, 0.2);
      color: #f99;
    }

    .badge.ballistic {
      background: rgba(255, 150, 50, 0.2);
      color: #fa5;
    }

    .empty-state {
      text-align: center;
      padding: 20px;
      opacity: 0.5;
      font-style: italic;
    }

    .damage-event {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 0;
      font-size: 11px;
    }

    .damage-arrow {
      color: #f66;
      font-weight: bold;
    }

    .damage-amount {
      color: #f99;
      font-weight: 600;
    }

    .link-btn {
      padding: 2px 6px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 3px;
      background: rgba(255, 255, 255, 0.08);
      color: #eee;
      cursor: pointer;
      font-size: 11px;
    }

    .link-btn:hover {
      background: rgba(255, 255, 255, 0.12);
    }
  `;

  @state() private combatDebugEnabled = false;
  @state() private combatState: CombatDebugState = {
    weapons: [],
    projectiles: [],
    recentDamage: [],
  };

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener(DebugUiBridge.uiEventName, this.handleCombatState);
  }

  disconnectedCallback(): void {
    window.removeEventListener(DebugUiBridge.uiEventName, this.handleCombatState);
    super.disconnectedCallback();
  }

  private handleCombatState = (event: Event): void => {
    const ce = event as CustomEvent;
    if (ce.detail?.type !== 'combat-debug-state') return;
    
    const data = ce.detail;
    this.combatState = {
      weapons: data.weapons || [],
      projectiles: data.projectiles || [],
      recentDamage: data.recentDamage || [],
      lastHit: data.lastHit ?? null,
    };
  };

  private toggleCombatDebug = (): void => {
    this.combatDebugEnabled = !this.combatDebugEnabled;
    DebugUiBridge.dispatchCommand({ type: 'combatDebug:set', enabled: this.combatDebugEnabled });
  };

  render() {
    const { weapons, projectiles, recentDamage, lastHit } = this.combatState;

    return html`
      <div class="panel-content">
        <!-- Combat Debug Toggle -->
        <div class="toggle-row">
          <span class="toggle-label">Show Hitboxes & Capsules</span>
          <div 
            class="toggle-switch ${this.combatDebugEnabled ? 'on' : ''}"
            @click=${this.toggleCombatDebug}
          ></div>
        </div>

        <!-- Last Hit -->
        <div class="section">
          <div class="section-title">Last Hit</div>
          ${lastHit?.nodeId
            ? html`
                <div class="item">
                  <div class="item-title">
                    <button class="link-btn" @click=${() => revealSceneNode(lastHit.nodeId)}>
                      ${lastHit.nodeName || lastHit.nodeId}
                    </button>
                  </div>
                  <div class="item-detail">Node ID: ${lastHit.nodeId}</div>
                </div>
              `
            : html`<div class="empty-state">No hits yet.</div>`}
        </div>

        <!-- Active Weapons (Melee) -->
        <div class="section">
          <div class="section-title">Active Weapons (${weapons.length})</div>
          ${weapons.length === 0 ? html`
            <div class="empty-state">No active weapons</div>
          ` : weapons.map(weapon => html`
            <div class="item">
              <div class="item-title">
                ${weapon.weaponMeshName || weapon.id}
                <span class="badge ${weapon.hitboxEnabled ? 'enabled' : 'disabled'}">
                  ${weapon.hitboxEnabled ? 'ACTIVE' : 'INACTIVE'}
                </span>
                ${weapon.hasHit ? html`<span class="badge" style="background: rgba(255,100,100,0.3); color: #f99;">HIT</span>` : ''}
              </div>
              <div class="item-detail">Owner: ${weapon.ownerId}</div>
              <div class="item-detail">Attack: ${weapon.attackDefId}</div>
              ${weapon.attachedBone ? html`
                <div class="item-detail">📍 Bone: ${weapon.attachedBone}</div>
              ` : ''}
              
              <!-- Collision Info -->
              <div class="item-detail" style="margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.05);">
                <span class="badge ${weapon.hasDedicatedCollisionMesh ? 'dedicated' : 'fallback'}">
                  ${weapon.hasDedicatedCollisionMesh ? '✓ Dedicated Collision' : '⚠ BBox Fallback'}
                </span>
              </div>
              ${weapon.collisionMeshName ? html`
                <div class="item-detail">Collision Mesh: ${weapon.collisionMeshName}</div>
              ` : html`
                <div class="item-detail" style="opacity: 0.7;">Using weapon mesh bounds for hitbox</div>
              `}
              
              <!-- Animation Info -->
              ${weapon.animationGroup ? html`
                <div class="item-detail" style="margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.05);">
                  🎬 Anim: ${weapon.animationGroup.name}
                  ${weapon.animationGroup.isPlaying ? html`
                    <span class="badge enabled">Playing</span>
                  ` : html`
                    <span class="badge disabled">Stopped</span>
                  `}
                  <span style="opacity: 0.6; font-size: 10px;"> (speed: ${weapon.animationGroup.speedRatio.toFixed(1)}x)</span>
                </div>
              ` : html`
                <div class="item-detail" style="opacity: 0.5;">No animation loaded</div>
              `}
            </div>
          `)}
        </div>

        <!-- Active Projectiles -->
        <div class="section">
          <div class="section-title">Active Projectiles (${projectiles.length})</div>
          ${projectiles.length === 0 ? html`
            <div class="empty-state">No active projectiles</div>
          ` : projectiles.map(projectile => html`
            <div class="item">
              <div class="item-title">
                ${projectile.id}
                <span class="badge ${projectile.type === 'kinematic' ? 'kinematic' : 'ballistic'}">
                  ${projectile.type.toUpperCase()}
                </span>
                ${projectile.hasCollided ? html`<span class="badge" style="background: rgba(255,200,100,0.3); color: #fc9;">COLLIDED</span>` : ''}
                ${projectile.detonated ? html`<span class="badge" style="background: rgba(255,100,100,0.3); color: #f99;">DETONATED</span>` : ''}
              </div>
              <div class="item-detail">Owner: ${projectile.ownerId}</div>
              <div class="item-detail">Attack: ${projectile.attackDefId}</div>
              <div class="item-detail">
                Position: (${projectile.position.x.toFixed(1)}, ${projectile.position.y.toFixed(1)}, ${projectile.position.z.toFixed(1)})
              </div>
              <div class="item-detail">
                Velocity: (${projectile.velocity.x.toFixed(1)}, ${projectile.velocity.y.toFixed(1)}, ${projectile.velocity.z.toFixed(1)})
              </div>
            </div>
          `)}
        </div>

        <!-- Recent Damage Events -->
        <div class="section">
          <div class="section-title">Recent Damage (Last 10)</div>
          ${recentDamage.length === 0 ? html`
            <div class="empty-state">No damage events yet</div>
          ` : recentDamage.slice(0, 10).map(event => {
            const timeAgo = Date.now() - event.timestamp;
            const timeAgoText = timeAgo < 1000 ? 'just now' : `${Math.floor(timeAgo / 1000)}s ago`;
            
            return html`
              <div class="damage-event">
                <span>${event.attackerId}</span>
                <span class="damage-arrow">→</span>
                <span>${event.targetId}</span>
                <span class="damage-amount">-${event.damage.toFixed(1)} HP</span>
                <span style="margin-left: auto; opacity: 0.6; font-size: 10px;">${timeAgoText}</span>
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'combat-panel': CombatPanel;
  }
}
